import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Cake } from "lucide-react";
import { OwnBirthday, fetchOwnBirthday, saveOwnBirthday } from "services/birthday.service";
import { listMonthNames, useUiLanguage } from "features/posts/utils/newsDates";

// 2000 is a leap year, so February offers the 29th.
const daysInMonth = (month: number): number => new Date(2000, month, 0).getDate();

// 16px on a phone: a smaller text makes iOS zoom the page in when the select gets focus.
const SELECT_CLASS =
  "mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-base md:text-sm max-md:min-h-11 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50";

/**
 * The signed-in person's own birthday (day and month only) and whether colleagues see it in the news.
 * Nothing is shown to colleagues until the box is ticked. Clearing the month removes the birthday.
 */
export function OwnBirthdayCard() {
  const { t } = useTranslation();
  const language = useUiLanguage();
  const monthNames = useMemo(() => listMonthNames(language), [language]);
  // The selects hold strings; '' means not chosen.
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [hasSavedBirthday, setHasSavedBirthday] = useState(false);
  const [loadStatus, setLoadStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [isSaving, setIsSaving] = useState(false);

  const showBirthday = useCallback((birthday: OwnBirthday | null) => {
    setMonth(birthday ? String(birthday.month) : "");
    setDay(birthday ? String(birthday.day) : "");
    setIsVisible(birthday?.isVisible ?? false);
    setHasSavedBirthday(birthday !== null);
  }, []);

  const loadBirthday = useCallback(() => {
    setLoadStatus("loading");
    fetchOwnBirthday()
      .then((birthday) => {
        showBirthday(birthday);
        setLoadStatus("ready");
      })
      .catch(() => setLoadStatus("failed"));
  }, [showBirthday]);

  useEffect(() => {
    loadBirthday();
  }, [loadBirthday]);

  const changeMonth = (value: string) => {
    setMonth(value);
    // A day the new month does not have (31 April) is dropped, and so is any day of an unset month.
    if (!value || Number(day) > daysInMonth(Number(value))) setDay("");
  };

  const hasDate = month !== "" && day !== "";
  const isCleared = month === "" && hasSavedBirthday;
  const canSave = loadStatus === "ready" && !isSaving && (hasDate || isCleared);

  const save = async () => {
    setIsSaving(true);
    try {
      const saved = await saveOwnBirthday(
        hasDate ? { month: Number(month), day: Number(day), isVisible } : { month: null, day: null }
      );
      showBirthday(saved);
      toast.success(t("birthday_saved"));
    } catch {
      toast.error(t("birthday_save_failed"));
    } finally {
      setIsSaving(false);
    }
  };

  const dayCount = month ? daysInMonth(Number(month)) : 0;
  const isLocked = loadStatus !== "ready" || isSaving;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="own-birthday">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Cake className="h-4 w-4 text-gray-500" />
        {t("birthday_card_title")}
      </h2>
      <p className="mb-3 mt-1 text-xs text-gray-600">{t("birthday_card_hint")}</p>

      {loadStatus === "failed" ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-red-700">
          <span>{t("birthday_load_failed")}</span>
          <button
            type="button"
            onClick={loadBirthday}
            className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-100 max-md:min-h-11"
          >
            {t("retry")}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <label className="text-sm font-medium text-gray-700">
              {t("birthday_month_label")}
              <select
                className={SELECT_CLASS}
                value={month}
                disabled={isLocked}
                onChange={(e) => changeMonth(e.target.value)}
              >
                <option value="">{t("birthday_not_set")}</option>
                {monthNames.map((name, index) => (
                  <option key={name} value={String(index + 1)}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              {t("birthday_day_label")}
              <select
                className={SELECT_CLASS}
                value={day}
                disabled={isLocked || month === ""}
                onChange={(e) => setDay(e.target.value)}
              >
                <option value="">{t("birthday_not_set")}</option>
                {Array.from({ length: dayCount }, (_, index) => (
                  <option key={index + 1} value={String(index + 1)}>
                    {index + 1}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-2 max-md:min-h-11">
            <input
              type="checkbox"
              checked={isVisible}
              disabled={isLocked || !hasDate}
              onChange={(e) => setIsVisible(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t("birthday_show_to_colleagues")}</span>
          </label>

          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11"
          >
            {t("save")}
          </button>
        </>
      )}
    </section>
  );
}
